import React, { useEffect, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, { PaginationProvider }  from "react-bootstrap-table2-paginator";
import { getAuthorities } from "../../../../../../_helpers/Helpers";
import * as actions from "../../../_redux/student-pre-interviews/StudentPreInterviewsAction";
import * as uiHelpers from "../StudentPreInterviewsUIHelpers";
import { 
  getSelectRow, 
  getHandlerTableChange,  
  NoRecordsFoundMessage, 
  PleaseWaitMessage, 
  sortCaret
} from "../../../../../../_metronic/_helpers";
import * as columnFormatters from "./column-formatters";
import { Pagination } from "../../../../../../_metronic/_partials/controls";
import { useStudentPreInterviewsUIContext } from "../StudentPreInterviewsUIContext";

export function StudentPreInterviewsTable({
  deleteStudentPreInterviews
}) {
  // Student PreInterviews UI Context
  const preInterviewsUIContext = useStudentPreInterviewsUIContext();
  const preInterviewsUIProps = useMemo(() => {
    return {
      ids: preInterviewsUIContext.ids,
      setIds: preInterviewsUIContext.setIds,
      queryParams: preInterviewsUIContext.queryParams,
      setQueryParams: preInterviewsUIContext.setQueryParams,
      openEditStudentPreInterviewPage: preInterviewsUIContext.openEditStudentPreInterviewPage,
      openFinalRegistrationPage: preInterviewsUIContext.openFinalRegistrationPage
    };
  }, [preInterviewsUIContext]);

  // Getting curret state of Student PreInterviews list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.studentPreInterviews }),
    shallowEqual
  );
  const { totalCount, entities, authorities, listLoading } = currentState;
  
  // Authority Parameters
  const { isEditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);
  
  // Student PreInterviews Redux state
  const dispatch = useDispatch();

  useEffect(() => {
    preInterviewsUIProps.setIds([]);
    dispatch(actions.getStudentPreInterviews(preInterviewsUIProps.queryParams));
    return () => dispatch(actions.cleanListState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preInterviewsUIProps.queryParams, dispatch]);

  // Table columns
  const columns = [
    {
      dataField: "personnel",
      text: "Personel",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "150px"
      }
    },
    {
      dataField: "date",
      text: "Görüşme Tarihi",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "160px"
      }
    },
    {
      dataField: "interviewTypeName",
      text: "Görüşme Türü",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "150px"
      }
    },
    {
      dataField: "relationshipName",
      text: "Veli Yakınlığı",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "150px"
      }
    },
    {
      dataField: "parentName",
      text: "Veli Adı Soyadı",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "200px"
      }
    },
    {
      dataField: "parentPhone",
      text: "Veli Telefonu",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "150px"
      }
    },
    {
      dataField: "explanation",
      text: "Açıklama",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "250px"
      }
    },
    {
      dataField: "studentName",
      text: "Öğrenci Adı Soyadı",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "200px"
      }
    },
    {
      dataField: "studentGrade",
      text: "Sınıfı",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "120px"
      }
    },
    {
      dataField: "studentSchool",
      text: "Okulu",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "200px"
      }
    },   
    {
      dataField: "trackingDate",
      text: "Takip Tarihi",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "150px"
      }
    },
    {
      dataField: "resultName",
      text: "Durum",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "135px"
      }
    },
    {
      dataField: "action",
      text: "İşlemler",
      formatter: columnFormatters.ActionsColumnFormatter,
      formatExtraData: {
        isEditionAuthorized,
        isDeletionAuthorized,
        openEditStudentPreInterviewPage: preInterviewsUIProps.openEditStudentPreInterviewPage,
        openFinalRegistrationPage: preInterviewsUIProps.openFinalRegistrationPage,
        deleteStudentPreInterviews
      },
      classes: "text-right pr-0",
      headerClasses: "text-right pr-10",
      style: {
        minWidth: "150px"
      }
    }
  ];

  // Table pagination properties
  const paginationOptions = {
    custom: true,
    totalSize: totalCount,
    sizePerPageList: uiHelpers.sizePerPageList,
    sizePerPage: preInterviewsUIProps.queryParams.pageSize,
    page: preInterviewsUIProps.queryParams.pageNumber
  };
  
  return (
    <PaginationProvider pagination={paginationFactory(paginationOptions)}>
      {({ paginationProps, paginationTableProps }) => {
        return (
          <Pagination
            isLoading={listLoading}
            paginationProps={paginationProps}
          >
            <BootstrapTable
              wrapperClasses="table-responsive"
              classes="table table-head-custom table-vertical-center overflow-hidden"
              bootstrap4
              bordered={false}
              remote
              keyField="id"
              data={entities ?? [] }
              columns={columns}
              defaultSorted={uiHelpers.defaultSorted}
              onTableChange={getHandlerTableChange(
                preInterviewsUIProps.setQueryParams
              )}
              selectRow={getSelectRow({
                entities,
                ids: preInterviewsUIProps.ids,
                setIds: preInterviewsUIProps.setIds,
                hidden: !isDeletionAuthorized
              })}
              {...paginationTableProps}
            >
              <PleaseWaitMessage entities={entities} />
              <NoRecordsFoundMessage entities={entities} />
            </BootstrapTable>
          </Pagination>
        );    
      }}
    </PaginationProvider>  
  );
}